/**
 * Helpers for deriving the "agent" and "user" display chips shown in the span
 * header (app/src/pages/SpanHeader.tsx).
 *
 * Span attributes reach the UI as a JSON blob in which dotted OpenInference keys
 * have been folded into nested objects on ingest, e.g. the flat attribute
 * `user.id = "u_123"` is stored as `{ "user": { "id": "u_123" } }`. A naive
 * `attributes["user"]` therefore yields an **object**, and rendering that object
 * directly as a React child throws ("Objects are not valid as a React child" /
 * minified React error #31).
 *
 * `coerceDisplayName` guarantees a `string | undefined` result for any input
 * shape, and `deriveSpanIdentity` looks the value up under every key spelling we
 * have seen, in priority order, using `??` so a present-but-empty value falls
 * through to the next candidate.
 */

/**
 * The `SpanHeader_span` fragment types `attributes` as `string` (a JSON blob),
 * but this helper intentionally also accepts an already-parsed object or a
 * nullish value: callers upstream sometimes hand it a parsed object, the field
 * has been nullable in the past, and a defensive signature keeps a malformed or
 * missing value from ever reaching the render path. Do not narrow this back to
 * `string` — `parseAttributes` handles every case without throwing.
 */
export type SpanAttributesInput =
  | string
  | Record<string, unknown>
  | null
  | undefined;

export interface SpanIdentity {
  /** Resolved agent name, or `undefined` when nothing usable is present. */
  agentName: string | undefined;
  /** Resolved user name/id, or `undefined` when nothing usable is present. */
  userName: string | undefined;
}

/**
 * Coerce an arbitrary attribute value into a safe, trimmed display string.
 *
 * - `string`            -> trimmed, or `undefined` when empty/whitespace-only
 * - finite `number`     -> its string form (`NaN`/`Infinity` -> `undefined`)
 * - `{ name | id }`     -> the first of `name` then `id` that is itself a
 *                          non-empty string; anything else -> `undefined`
 * - arrays / functions / nested objects / `null` / `undefined` -> `undefined`
 *
 * The return type is intentionally `string | undefined` and never an object, so
 * the result is always safe to use as a React child.
 */
export function coerceDisplayName(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value.trim() || undefined;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const inner = obj.name ?? obj.id;
    if (typeof inner === "string") {
      return inner.trim() || undefined;
    }
    if (typeof inner === "number" && Number.isFinite(inner)) {
      return String(inner);
    }
    return undefined;
  }
  return undefined;
}

/** Parse the attributes blob defensively; never throws. */
function parseAttributes(attributes: SpanAttributesInput): Record<string, any> {
  if (!attributes) {
    return {};
  }
  if (typeof attributes === "string") {
    try {
      const parsed = JSON.parse(attributes);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }
  return Array.isArray(attributes)
    ? {}
    : (attributes as Record<string, any>);
}

/**
 * Resolve the agent and user display names from a span's `attributes`.
 *
 * Accepts the raw JSON string, an already-parsed object, or nullish. Both
 * returned fields are `string | undefined` — safe to render, and `undefined`
 * signals "no value, render nothing" rather than a fabricated placeholder.
 */
export function deriveSpanIdentity(
  attributes: SpanAttributesInput
): SpanIdentity {
  const p = parseAttributes(attributes);
  const metadata =
    p.metadata && typeof p.metadata === "object" ? p.metadata : undefined;

  // Key spellings seen in the wild, highest priority first. `??` (not `||`) so a
  // present-but-empty value falls through to the next candidate.
  const agentName =
    coerceDisplayName(p["agent.name"]) ?? //          flat OpenInference-style key
    coerceDisplayName(p["agent_name"]) ?? //          snake_case variant
    coerceDisplayName(p.agent?.name) ?? //            `agent.name` folded to { agent: { name } } on ingest
    coerceDisplayName(p.agent) ?? //                  `agent` set to a bare string/id
    coerceDisplayName(p["metadata.agent_name"]) ?? // flat key kept under metadata
    coerceDisplayName(metadata?.agent_name) ?? //     nested metadata (framework-dependent)
    coerceDisplayName(metadata?.["agent.name"]);

  const userName =
    coerceDisplayName(p["user.name"]) ?? //           flat key (rare; not standard OpenInference)
    coerceDisplayName(p["username"]) ?? //            common flat spelling
    coerceDisplayName(p["user_name"]) ?? //           snake_case variant
    coerceDisplayName(p.user?.name) ?? //             `user.name` folded to { user: { name } }
    coerceDisplayName(p.user?.id) ?? //               OpenInference `user.id` folded to { user: { id } } — the AlterGate case
    coerceDisplayName(p.user) ?? //                   `user` set to a bare string/id
    coerceDisplayName(p["metadata.user_id"]) ?? //    LiteLLM/AlterGate also writes this flat key
    coerceDisplayName(metadata?.user_id) ?? //        nested metadata
    coerceDisplayName(metadata?.username) ??
    coerceDisplayName(metadata?.user_api_key_user_id) ?? //    LiteLLM key metadata
    coerceDisplayName(metadata?.user_api_key_user_email);

  return { agentName, userName };
}
